import { useState, useRef, useEffect } from 'preact/hooks';
import { Button } from '@/components/ui/button';
import { Terminal as TerminalIcon, Minimize2, Maximize2 } from 'lucide-preact';
import Terminal from './Terminal';

interface PersistentTerminalProps {
  currentProject: {
    id: string;
    name: string;
    path: string;
    type: string;
  };
  onTerminalCommand?: (command: string) => void;
  terminalOutput?: string;
  isTerminalConnected: boolean;
}

export function PersistentTerminal({
  currentProject,
  onTerminalCommand,
  terminalOutput,
  isTerminalConnected
}: PersistentTerminalProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="terminal-fixed">
      {/* Minimized View */}
      {!isExpanded && (
        <div className="flex items-center justify-between p-2 bg-muted border-b">
          <div className="flex items-center gap-2 text-sm">
            <TerminalIcon className="h-4 w-4" />
            <span className="font-medium">{currentProject.name} Terminal</span>
            {isTerminalConnected ? (
              <span className="text-green-500 text-xs">● Connected</span>
            ) : (
              <span className="text-red-500 text-xs">● Disconnected</span>
            )}
          </div>
          <Button
            onClick={() => setIsExpanded(true)}
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
          >
            <Maximize2 className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="flex h-96">
          {/* Terminal Interface */}
          <div className="flex-1 flex flex-col">
            {/* Terminal Header */}
            <div className="flex items-center justify-between p-2 bg-muted border-b">
              <div className="flex items-center gap-2 text-sm">
                <TerminalIcon className="h-4 w-4" />
                Terminal - {currentProject.name}
                {isTerminalConnected ? (
                  <span className="text-green-500 text-xs">● Connected</span>
                ) : (
                  <span className="text-red-500 text-xs">● Disconnected</span>
                )}
              </div>
              <Button
                onClick={() => setIsExpanded(false)}
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
              >
                <Minimize2 className="h-3 w-3" />
              </Button>
            </div>

            {/* Terminal Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0">
                <Terminal
                  session={null}
                  onQuery={(query) => {
                    if (onTerminalCommand) {
                      onTerminalCommand(query);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}